import {
	currentTargetRequired,
	getAttributeOrThrow,
	ON_CHANGE_DEBOUNCE_TIMER,
	qsaRequired,
	qsOptional,
	removeTrapFocus,
	trapFocus,
} from '@/scripts/core/global'
import { routes } from '@/scripts/setup'
import { debounce, qsRequired } from '@/scripts/core/global'
import { UcoastEl } from '@/scripts/core/UcoastEl'
import EmblaCarousel, { type EmblaCarouselType, type EmblaOptionsType } from 'embla-carousel'
import { WheelGesturesPlugin } from 'embla-carousel-wheel-gestures'
import ClassNames from 'embla-carousel-class-names'
import type { CartDrawer } from '@/scripts/cart/cart-drawer'
import type { CartUpdateWithSections } from '@/scripts/core/cart-functions'
import { enqueueCartOperation } from '@/scripts/cart/cart-queue'

const CART_SECTIONS = ['cart-drawer', 'cart-icon-bubble']

export class CartSamples extends UcoastEl {
	static htmlSelector = 'cart-samples'
	input?: HTMLTextAreaElement
	summary: HTMLElement
	details: HTMLDetailsElement
	selectedSamplesCount: HTMLElement
	boundOnKeyUpEscape: (event: KeyboardEvent) => void
	samples: NodeListOf<HTMLButtonElement>
	selectedSamples: number[] = []
	doUpdate: (event: Event) => void
	emblaApi?: EmblaCarouselType
	constructor() {
		super()
		this.input = qsOptional('textarea', this)
		this.summary = qsRequired('summary', this)
		this.details = qsRequired('details', this)
		this.boundOnKeyUpEscape = this.onKeyUpEscape.bind(this)
		this.summary.addEventListener('click', this.onSummaryClick.bind(this))
		this.samples = qsaRequired('[data-uc-sample]', this)
		this.selectedSamplesCount = qsRequired('[data-samples-selected]', this)
		this.samples.forEach((sample) => {
			sample.addEventListener('click', this.selectSample.bind(this))
		})
		this.initialize()
		this.doUpdate = debounce((_: Event) => {
			const updates = this.createUpdateObject()
			void enqueueCartOperation(
				() =>
					fetch(`${routes.cart_update_url}`, {
						method: 'POST',
						headers: {
							'Content-Type': 'application/json',
						},
						body: JSON.stringify({
							updates,
							sections: CART_SECTIONS,
							sections_url: window.location.pathname,
						}),
					})
						.then((response) => response.json())
						.then((parsedState: CartUpdateWithSections) => {
							;(this.getCartDrawer() as CartDrawer).renderContentsWithoutOpening(parsedState)
						})
						.catch((e) => {
							console.error(e)
						}),
			)
		}, ON_CHANGE_DEBOUNCE_TIMER).bind(this)
	}

	initialize() {
		this.samples.forEach((sample) => {
			const variantId = parseInt(
				getAttributeOrThrow('data-uc-sample', sample)
			)
			if (sample.classList.contains('active')) {
				this.selectedSamples.push(variantId)
			}
		})
		this.toggleActiveSamples()
		if (this.selectedSamples.length === 0) {
			this.autoSelectDefaults()
		}
		if (this.isOpen()) {
			const height = this.details.scrollHeight
			if (height > 0) {
				this.details.style.maxHeight = `${height}px`
			}
			this.getCartDrawer().classList.add('sample-active')
			window.setTimeout(() => {
				window.Ucoast.mediaManager.loadAllInContainer(this)
			}, 4)
		}
		this.updateFixedAreaHeight()
		this.initEmbla()
	}

	initEmbla() {
		const viewportNode = qsOptional('.embla__viewport', this)
		if (!viewportNode) return
		const OPTIONS: EmblaOptionsType = {
			align: 'start',
			containScroll: 'keepSnaps',
			dragFree: true,
			duration: 100,
			dragThreshold: 100,
		}
		try {
			this.emblaApi = EmblaCarousel(viewportNode, OPTIONS, [
				WheelGesturesPlugin(),
				ClassNames(),
			])
		} catch (e) {
			console.error(e)
		}
	}

	autoSelectDefaults() {
		const raw = this.getAttribute('data-default-samples')
		if (!raw) return
		const allIds = this.getAllIds()
		const defaultIds = raw
			.split(',')
			.map((s) => parseInt(s, 10))
			.filter((id) => !isNaN(id) && allIds.includes(id))
		if (defaultIds.length === 0) return
		this.selectedSamples = defaultIds
		this.toggleActiveSamples()
		const updates: Record<number, number> = {}
		allIds.forEach((id) => {
			updates[id] = defaultIds.includes(id) ? 1 : 0
		})
		void enqueueCartOperation(
			() =>
				fetch(`${routes.cart_update_url}`, {
					method: 'POST',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify({
						updates,
						sections: CART_SECTIONS,
						sections_url: window.location.pathname,
					}),
				})
					.then((response) => response.json())
					.then((parsedState: CartUpdateWithSections) => {
						;(this.getCartDrawer() as CartDrawer).renderContentsWithoutOpening(parsedState)
					})
					.catch((e) => console.error(e)),
		)
	}

	createUpdateObject() {
		const updateObject: Record<number, number> = {}
		const ids = this.getAllIds()
		ids.forEach((id) => {
			if (this.selectedSamples.includes(id)) {
				updateObject[id] = 1
			} else {
				updateObject[id] = 0
			}
		})
		return updateObject
	}

	getAllIds() {
		return Array.from(this.samples).map((sample) => {
			return parseInt(getAttributeOrThrow('data-uc-sample', sample))
		})
	}

	selectSample(event: Event) {
		event.preventDefault()
		const target = currentTargetRequired(event)
		const variantId = parseInt(
			getAttributeOrThrow('data-uc-sample', target)
		)
		if (this.selectedSamples.includes(variantId)) {
			this.selectedSamples = this.selectedSamples.filter(
				(id) => id !== variantId
			)
		} else if (this.selectedSamples.length < 2) {
			this.selectedSamples.push(variantId)
		}
		this.toggleActiveSamples()
		this.doUpdate(event)
	}
	toggleActiveSamples() {
		this.samples.forEach((sample) => {
			const variantId = parseInt(
				getAttributeOrThrow('data-uc-sample', sample)
			)
			if (this.selectedSamples.includes(variantId)) {
				sample.classList.add('active')
				sample.removeAttribute('disabled')
				return
			}

			sample.classList.remove('active')
			if (this.selectedSamples.length > 1) {
				sample.setAttribute('disabled', '')
			} else {
				sample.removeAttribute('disabled')
			}
		})
		this.selectedSamplesCount.textContent =
			this.selectedSamples.length.toString()
	}
	onSummaryClick(event: Event) {
		event.preventDefault()
		return this.isOpen() ? this.close() : this.open()
	}
	getCartDrawer() {
		return qsRequired('cart-drawer')
	}
	getSummaryHeight() {
		return this.summary.scrollHeight
	}
	isOpen() {
		return this.details.open
	}
	open() {
		this.details.open = true
		this.details.style.maxHeight = `${this.details.scrollHeight}px`
		this.getCartDrawer().classList.add('sample-active')
		this.updateFixedAreaHeight()
		trapFocus(this.details)
		this.details.addEventListener('keyup', this.boundOnKeyUpEscape)
		window.setTimeout(() => {
			window.Ucoast.mediaManager.loadAllInContainer(this)
		}, 4);
	}
	close() {
		this.details.style.maxHeight = `${this.getSummaryHeight()}px`
		this.getCartDrawer().classList.remove('sample-active')
		window.setTimeout(() => {
			this.details.open = false
			this.updateFixedAreaHeight()
		}, 301)
		removeTrapFocus(this.summary)
	}
	updateFixedAreaHeight() {
		const drawer = this.getCartDrawer()
		if (this.isOpen()) {
			const expandedOffset = this.details.scrollHeight - this.getSummaryHeight()
			drawer.style.setProperty('--samples-expanded-offset', `${expandedOffset}px`)
		} else {
			drawer.style.setProperty('--samples-expanded-offset', '0px')
		}
	}
	onKeyUpEscape(event: KeyboardEvent) {
		if (event.code.toUpperCase() !== 'ESCAPE') return
		if (!this.isOpen()) return
		event.stopPropagation()
		this.details.removeEventListener('keyup', this.boundOnKeyUpEscape)
		this.close()
	}
}
